import {
  hasDotSegment,
  type ProxyModel,
  type ReadPolicy,
} from '../../hooks/useProxyAdmin';
import {
  PUBLIC_MODEL_GROUP,
  READ_TAG_PATTERN,
} from '../../utils/middlemanRules';
import { changedFields } from './fieldChanges';

export type Tri = '' | 'true' | 'false';

export const COST_FIELDS = [
  ['input_cost_per_1m', 'Input $/1M'],
  ['output_cost_per_1m', 'Output $/1M'],
  ['cache_read_input_cost_per_1m', 'Cache read $/1M'],
  ['cache_write_input_cost_per_1m', 'Cache write $/1M'],
] as const;
export const INT_FIELDS = [
  ['context_length', 'Context length'],
  ['output_limit', 'Output limit'],
  ['concurrency_limit', 'Concurrency limit'],
] as const;
type CostField = (typeof COST_FIELDS)[number][0];
type IntField = (typeof INT_FIELDS)[number][0];

/** Config fields edited as JSON rather than through the form's own inputs. */
export const ADVANCED_FIELDS = [
  'max_tokens_keyword',
  'request_timeout_minutes',
  'limits',
  'override_url',
  'force_tokenizer',
  'lab_documentation_url',
  'base_model_info_key',
  'return_request_id',
  'anthropic_account',
  'openai_account',
] as const;

/** Hawk exempts this group's transcripts from read policies. */
export const PUBLIC_GROUP = PUBLIC_MODEL_GROUP;
const READ_TAG_RE = new RegExp(READ_TAG_PATTERN);
/** What a model with no stored policy gets. */
export const DEFAULT_READ_POLICY: ReadPolicy = {
  mode: 'provider',
  tags: [],
  zdr_only: false,
};

/** Tags typed as a comma- or space-separated list: sorted and de-duplicated, as middleman stores them. */
export const parseTags = (text: string) =>
  [...new Set(text.split(/[\s,]+/).filter(Boolean))].sort();

const isDefaultPolicy = (policy: ReadPolicy) =>
  policy.mode === 'provider' && !policy.zdr_only;

/**
 * `anthropic-beta` flags typed as a comma- or space-separated list (a flag can
 * hold neither), de-duplicated in first-seen order, as middleman stores them.
 */
export const parseFlags = (text: string) => [
  ...new Set(text.split(/[\s,]+/).filter(Boolean)),
];

/**
 * Why a draft's required beta flags can't be saved, mirroring middleman: each
 * is visible ASCII, and only a lab served by the Anthropic API honours them.
 * `labApi` is undefined when the lab's API isn't known (labs still loading).
 */
function betasProblem(
  flags: string[] | null,
  labApi: string | undefined
): string | null {
  if (!flags) return null;
  const bad = flags.filter(f => !/^[!-~]+$/.test(f));
  if (bad.length)
    return `Beta flags must be visible ASCII characters: ${bad.join(', ')}.`;
  if (labApi !== undefined && labApi !== 'anthropic')
    return 'Required Anthropic beta flags only work on a lab served by the Anthropic API: clear them or choose an Anthropic lab.';
  return null;
}

/** Who a policy lets read the model's non-public transcripts, in words. */
export function describePolicy(policy: ReadPolicy): string {
  const who =
    policy.mode === 'any'
      ? 'any reader model'
      : policy.mode === 'provider'
        ? 'reader models served by the same provider'
        : policy.tags.length
          ? `reader models holding ${policy.tags.length === 1 ? 'the tag' : 'any of the tags'} ${policy.tags.join(', ')}`
          : 'no reader model (no tags given)';
  return policy.zdr_only ? `${who}, if recorded as zero-data-retention` : who;
}

/**
 * Why a draft's read access can't be saved, mirroring middleman's rules: tags
 * are lowercase letters, digits and hyphens; a tags policy needs a tag; and a
 * public model keeps the default (Hawk doesn't restrict public transcripts).
 */
function readAccessProblem(
  policy: ReadPolicy,
  readerTags: string[],
  group: string
): string | null {
  const bad = [...policy.tags, ...readerTags].filter(t => !READ_TAG_RE.test(t));
  if (bad.length)
    return `Tags must be lowercase letters, digits and hyphens, starting with a letter or digit: ${bad.join(', ')}.`;
  if (policy.mode === 'tags' && policy.tags.length === 0)
    return 'A read policy limited to tagged readers needs at least one tag.';
  if (group === PUBLIC_GROUP && !isDefaultPolicy(policy))
    return `A model in ${PUBLIC_GROUP} keeps the default read policy: Hawk doesn’t restrict public models’ transcripts.`;
  return null;
}

/**
 * Stored settings the loader can't use as written: middleman reports a cleaned
 * value (null betas, a fail-closed policy, the valid tags) plus the error, and
 * what that does. All three are advanced fields.
 */
export const MODEL_FAULTS = [
  {
    field: 'required_anthropic_betas',
    error: 'required_anthropic_betas_error',
    label: 'Required Anthropic betas',
    effect: 'Middleman refuses every request to the model until it is fixed.',
  },
  {
    field: 'read_policy',
    error: 'read_policy_error',
    label: 'Read policy',
    // Not "nobody": only a policy malformed as a whole fails closed. Invalid
    // tags are dropped and the valid ones still grant access, so say what
    // middleman enforces (the policy the admin API reports).
    effect: (model: ProxyModel) =>
      `Middleman enforces it as cleaned: ${describePolicy(model.read_policy)} may read the model’s non-public transcripts.`,
  },
  {
    field: 'reader_tags',
    error: 'reader_tags_error',
    label: 'Reader tags',
    effect: 'Invalid tags are dropped, so they grant nothing.',
  },
] as const;
export type FaultField = (typeof MODEL_FAULTS)[number]['field'];

/** The model's faulted settings, each with its stored error and what it does now. */
export const modelFaults = (model: ProxyModel) =>
  MODEL_FAULTS.flatMap(fault =>
    model[fault.error]
      ? [
          {
            ...fault,
            message: model[fault.error]!,
            effect:
              typeof fault.effect === 'function'
                ? fault.effect(model)
                : fault.effect,
          },
        ]
      : []
  );

export interface ModelDraft extends Record<CostField | IntField, string> {
  public_name: string;
  group: string;
  lab: string;
  /** '' is unrecorded. */
  provider: string;
  is_zdr: Tri;
  raw_cot: Tri;
  dead: boolean;
  vision: boolean;
  stream: boolean;
  /** '' is no alias: the public name. */
  danger_name: string;
  are_details_secret: Tri;
  private_name: string;
  comments: string;
  /** Who may read the model's non-public transcripts (its read policy). */
  read_mode: ReadPolicy['mode'];
  /** The policy's tags, comma- or space-separated; used in `tags` mode only. */
  read_tags: string;
  read_zdr_only: boolean;
  /** The tags this model holds as a reader of others' transcripts. */
  reader_tags: string;
  /** Flags every request must carry in `anthropic-beta`; '' is no requirement. */
  anthropic_betas: string;
  advanced: string;
  /**
   * Faulted settings to rewrite from the advanced JSON even when its value
   * matches the cleaned one middleman reported: comparing against that would
   * drop the repair as "unchanged" and leave the stored value broken.
   */
  repair: FaultField[];
}

const tri = (value: boolean | null): Tri =>
  value === null ? '' : value ? 'true' : 'false';
const str = (value: unknown) =>
  value === null || value === undefined ? '' : String(value);

export function draftFromModel(model: ProxyModel | null): ModelDraft {
  if (!model) {
    return {
      public_name: '',
      group: '',
      lab: '',
      provider: '',
      is_zdr: '',
      raw_cot: '',
      dead: false,
      vision: false,
      stream: false,
      danger_name: '',
      are_details_secret: '',
      private_name: '',
      comments: '',
      read_mode: 'provider',
      read_tags: '',
      read_zdr_only: false,
      reader_tags: '',
      anthropic_betas: '',
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      cache_read_input_cost_per_1m: '',
      cache_write_input_cost_per_1m: '',
      context_length: '',
      output_limit: '',
      concurrency_limit: '',
      advanced: '{}',
      repair: [],
    };
  }
  return {
    public_name: model.public_name,
    group: model.group,
    lab: model.lab,
    provider: model.provider ?? '',
    is_zdr: tri(model.is_zdr),
    raw_cot: tri(model.raw_cot),
    dead: model.dead,
    vision: model.vision,
    stream: model.stream,
    danger_name:
      model.danger_name === model.public_name ? '' : (model.danger_name ?? ''),
    are_details_secret: tri(model.are_details_secret),
    private_name: model.private_name ?? '',
    comments: model.comments ?? '',
    // As the loader enforces it (a malformed stored policy reads as nobody).
    read_mode: model.read_policy.mode,
    read_tags: model.read_policy.tags.join(', '),
    read_zdr_only: model.read_policy.zdr_only,
    reader_tags: model.reader_tags.join(', '),
    // A malformed stored value reads as none (with an error to repair).
    anthropic_betas: (model.required_anthropic_betas ?? []).join(', '),
    input_cost_per_1m: str(model.input_cost_per_1m),
    output_cost_per_1m: str(model.output_cost_per_1m),
    cache_read_input_cost_per_1m: str(model.cache_read_input_cost_per_1m),
    cache_write_input_cost_per_1m: str(model.cache_write_input_cost_per_1m),
    context_length: str(model.context_length),
    output_limit: str(model.output_limit),
    concurrency_limit: str(model.concurrency_limit),
    advanced: JSON.stringify(
      Object.fromEntries(ADVANCED_FIELDS.map(f => [f, model[f]])),
      null,
      2
    ),
    repair: [],
  };
}

type Built = { body: Record<string, unknown> } | { error: string };

/** The config fields the draft states; omitted keys say nothing. */
function draftFields(
  draft: ModelDraft
): { fields: Record<string, unknown> } | { error: string } {
  let advanced: unknown;
  try {
    advanced = JSON.parse(draft.advanced.trim() || '{}');
  } catch (e) {
    return { error: `Advanced JSON is not valid: ${(e as Error).message}` };
  }
  if (!advanced || typeof advanced !== 'object' || Array.isArray(advanced))
    return { error: 'Advanced JSON must be an object.' };
  for (const key of Object.keys(advanced)) {
    if (!(ADVANCED_FIELDS as readonly string[]).includes(key))
      return {
        error: `Advanced JSON: ${key} isn't an advanced field${
          key in draft ? '; set it in the form instead' : ''
        }.`,
      };
  }

  const fields: Record<string, unknown> = {};
  for (const [field, label] of COST_FIELDS) {
    const text = draft[field].trim();
    const value = text === '' ? null : Number(text);
    if (value !== null && !(Number.isFinite(value) && value >= 0))
      return { error: `${label} must be a number of at least 0.` };
    fields[field] = value;
  }
  for (const [field, label] of INT_FIELDS) {
    const text = draft[field].trim();
    const value = text === '' ? null : Number(text);
    if (value !== null && !(Number.isInteger(value) && value > 0))
      return { error: `${label} must be a whole number above 0.` };
    fields[field] = value;
  }
  if (
    (fields.input_cost_per_1m === null) !==
    (fields.output_cost_per_1m === null)
  )
    return { error: 'Set both input and output cost, or neither.' };
  const group = draft.group.trim();
  if (group.startsWith('model-access-') && group.includes(':'))
    return {
      error: 'Group must not include a permission suffix such as :read.',
    };

  Object.assign(fields, {
    group,
    lab: draft.lab,
    dead: draft.dead,
    vision: draft.vision,
    stream: draft.stream,
    private_name: draft.private_name.trim() || null,
    comments: draft.comments.trim() || null,
    read_policy: {
      mode: draft.read_mode,
      // Tags only mean something in `tags` mode: don't keep hidden ones.
      tags: draft.read_mode === 'tags' ? parseTags(draft.read_tags) : [],
      zdr_only: draft.read_zdr_only,
    } satisfies ReadPolicy,
    reader_tags: parseTags(draft.reader_tags),
    // No requirement is unset, never an empty list (middleman refuses []).
    required_anthropic_betas: parseFlags(draft.anthropic_betas).length
      ? parseFlags(draft.anthropic_betas)
      : null,
    ...(advanced as Record<string, unknown>),
  });
  return { fields };
}

/** Read access as the draft states it, checked against middleman's rules. */
const readAccessOf = (fields: Record<string, unknown>) =>
  readAccessProblem(
    fields.read_policy as ReadPolicy,
    fields.reader_tags as string[],
    fields.group as string
  );

/** A new model's body, as `POST /models/` takes it. */
/**
 * `labApi`: the chosen lab's passthrough API, when known, for the checks that
 * depend on it (beta flags need an Anthropic lab).
 */
export function buildCreateBody(draft: ModelDraft, labApi?: string): Built {
  const built = draftFields(draft);
  if ('error' in built) return built;
  const publicName = draft.public_name.trim();
  if (!publicName || !draft.group.trim() || !draft.lab)
    return { error: 'Public name, group and lab are required.' };
  if (hasDotSegment(publicName))
    return {
      error:
        "The public name can't have a '.' or '..' segment: no URL could address the model afterwards.",
    };
  if (draft.is_zdr === '' || draft.raw_cot === '')
    return {
      error:
        'Record whether the model is zero-data-retention and whether it returns raw chain-of-thought.',
    };
  const dangerName = draft.danger_name.trim();
  if (
    dangerName &&
    dangerName !== publicName &&
    draft.are_details_secret === ''
  )
    return {
      error:
        'The upstream name differs from the public name: choose whether its details are secret.',
    };
  const readProblem =
    readAccessOf(built.fields) ??
    betasProblem(
      built.fields.required_anthropic_betas as string[] | null,
      labApi
    );
  if (readProblem) return { error: readProblem };
  // Unset values are left out, so the server's defaults apply: no tags, and
  // the default read policy.
  const body = Object.fromEntries(
    Object.entries(built.fields).filter(
      ([key, v]) =>
        v !== null &&
        !(key === 'reader_tags' && (v as string[]).length === 0) &&
        !(key === 'read_policy' && isDefaultPolicy(v as ReadPolicy))
    )
  );
  return {
    body: {
      public_name: publicName,
      ...body,
      is_zdr: draft.is_zdr === 'true',
      raw_cot: draft.raw_cot === 'true',
      ...(draft.provider && { provider: draft.provider }),
      ...(dangerName && { danger_name: dangerName }),
      ...(draft.are_details_secret !== '' && {
        are_details_secret: draft.are_details_secret === 'true',
      }),
    },
  };
}

/** The stored model as the form compares it: costs as numbers. */
export function comparableModel(model: ProxyModel): Record<string, unknown> {
  const before: Record<string, unknown> = { ...model };
  for (const [field] of COST_FIELDS) {
    const value = model[field];
    before[field] = value === null ? null : Number(value);
  }
  return before;
}

/** Only the fields a draft changes, as `PATCH /models/{name}` takes them. */
/** `labApi`: the chosen lab's passthrough API, when known (see buildCreateBody). */
export function buildPatch(
  draft: ModelDraft,
  model: ProxyModel,
  labApi?: string
): Built {
  const built = draftFields(draft);
  if ('error' in built) return built;
  if (!built.fields.group || !draft.lab)
    return { error: 'Group and lab are required.' };
  if (draft.provider === '' && model.provider !== null)
    return { error: 'A recorded provider can be corrected but not cleared.' };
  const after: Record<string, unknown> = {
    ...built.fields,
    is_zdr: draft.is_zdr === 'true',
    // A raw_cot still unrecorded stays so: it can't be set back to null.
    ...(draft.raw_cot !== '' && { raw_cot: draft.raw_cot === 'true' }),
    ...(draft.provider && { provider: draft.provider }),
    danger_name: draft.danger_name.trim() || model.public_name,
    are_details_secret: draft.are_details_secret === 'true',
  };
  const patch = changedFields(comparableModel(model), after);
  // A model aliased to another upstream name must state its secrecy, and the
  // stored config may not record it.
  if ('danger_name' in patch && patch.danger_name !== model.public_name)
    patch.are_details_secret = after.are_details_secret;
  // An explicit repair is sent whatever the comparison says (a field left out
  // of the JSON clears it). Only faults the admin chose: untouched faulted
  // settings are never rewritten behind their back.
  for (const field of draft.repair) {
    if (modelFaults(model).some(f => f.field === field))
      patch[field] = after[field] ?? null;
  }
  // Checked only when read access (or the group it depends on) changes: a
  // malformed stored policy reads as one nobody can read under, which mustn't
  // block unrelated edits (the repair checkbox fixes it).
  // Likewise beta flags, checked when they or the lab they depend on change.
  if (['required_anthropic_betas', 'lab'].some(key => key in patch)) {
    const betaProblem = betasProblem(
      after.required_anthropic_betas as string[] | null,
      labApi
    );
    if (betaProblem) return { error: betaProblem };
  }
  if (['read_policy', 'reader_tags', 'group'].some(key => key in patch)) {
    const readProblem = readAccessOf(after);
    if (readProblem) return { error: readProblem };
  }
  // Back to the default: clear it, as middleman stores no policy for one.
  if (
    patch.read_policy &&
    isDefaultPolicy(patch.read_policy as ReadPolicy) &&
    !draft.repair.includes('read_policy')
  )
    patch.read_policy = null;
  if (Object.keys(patch).length === 0) return { error: 'Nothing changed.' };
  return { body: patch };
}
